import React from 'react';
import { useAsync } from 'react-use';
import styled from '@emotion/styled';
import { withTheme } from '@emotion/react';
import { Theme, ThemeProps } from '@theme/base';
import { boxShadowMixins } from '@styles/mixins';
import { ImageApi, Category } from '@model/iceberg';
import { TripTypes } from '@model/common';
import { DestinationLink } from '@components/common/next-link';
import { LazyLoadImage } from '@components/common/lazy-load';
import { RippleCard } from '@components/common/ripple-card';
import { TestId } from '@components/test-ids';
import { useI18NextContext } from '@components/hooks';
import { TILE_IMAGES_FALLBACK } from '@mock/fallback';

/* ***************** *
 *       Types       *
 * ***************** */
export interface TileProps {
  name: string;
  path: string;
  count?: number;
  theme?: Theme;
  testId?: string;
  tripType?: TripTypes;
}

/* ***************** *
 *       Styles      *
 * ***************** */

const OuterContainer: any = styled(RippleCard)(({ theme }: ThemeProps) => ({
  borderRadius: theme.custom.spacing.small,
  overflow: 'hidden',
  position: 'relative',
  cursor: 'pointer',
  boxShadow: boxShadowMixins(theme.custom.colors.group10.base).light,
  paddingBottom: '50%'
}));

const BackgroundImage: any = styled(LazyLoadImage)(({ theme }: ThemeProps) => ({
  position: 'relative',
  backgroundColor: theme.custom.colors.group10.light,
  transition: 'all 0.5s ease',
  WebkitUserSelect: 'none' /* disable selection/Copy of UIWebView */,
  WebkitTouchCallout: 'none' /* disable the IOS popup when long-press on a link */,
  ':hover': {
    transform: 'scale(1.2)'
  }
}));

const ResortLabel = styled.div(({ theme }: ThemeProps) => ({
  borderRadius: 6,
  position: 'absolute',
  bottom: theme.custom.spacing.large,
  left: theme.custom.spacing.large,
  padding: 10,
  backgroundColor: theme.custom.colors.overlay.dark,
  backdropFilter: 'blur(10px)',
  ...(theme.custom.typography.h5 as any),
  color: theme.custom.colors.white
}));

const HotelsCount = styled.span(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.labels.default as any),
  fontWeight: 500,
  color: theme.custom.colors.white,
  paddingLeft: theme.custom.spacing.xSmall
}));

/* ******************************** *
 *    TileComponent    *
 * ******************************** */

export const TileComponent = (props: TileProps) => {
  const t: any = useI18NextContext();
  const { name, count, path, tripType } = props;

  const { value } = useAsync(async () => {
    return await new ImageApi().images({
      category: Category.SCENERY,
      path
    });
  }, []);

  const imageUrl: string = value && value.images?.length ? value.images[0] : TILE_IMAGES_FALLBACK[0];

  return (
    <DestinationLink path={path} tripType={tripType}>
      <OuterContainer data-testid={props.testId || TestId.exploreResort.exploreResortTile.main}>
        <BackgroundImage src={imageUrl} />
        <ResortLabel data-testid={TestId.exploreResort.exploreResortTile.label}>
          {name}
          {count && (
            <HotelsCount data-testid={TestId.exploreResort.exploreResortTile.hotelsCount}>
              ({t('explore-resorts__tiles--hotel-count', { count: count })})
            </HotelsCount>
          )}
        </ResortLabel>
      </OuterContainer>
    </DestinationLink>
  );
};

export const Tile = withTheme(TileComponent);
